import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../../context/AuthContext";
import { exchangeGoogleDriveToken } from "../../../../../services/socialTokenService";
import {
  GOOGLE_DRIVE_SESSION,
  getGoogleDriveOAuthRedirectUri,
  rememberGoogleDriveProfileId,
} from "../../../../media/providers/googleDrive/googleDriveEnv";

/** Inline OAuth round-trip: chat → Google consent → here → back to chat. */
const GoogleDriveCallback: React.FC = () => {
  const navigate = useNavigate();
  const { idToken, isLoading, isAuthenticated } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const exchangeStartedRef = useRef(false);

  useEffect(() => {
    if (isLoading || done || exchangeStartedRef.current) return;

    const params = new URLSearchParams(window.location.search);
    const errParam = params.get("error");
    if (errParam) {
      setError(`Google authorization was cancelled or failed (${errParam}).`);
      return;
    }

    const code = params.get("code");
    const state = params.get("state");
    const customerId = sessionStorage.getItem(GOOGLE_DRIVE_SESSION.CUSTOMER_ID);
    const returnTo = sessionStorage.getItem(GOOGLE_DRIVE_SESSION.RETURN_TO) || "/";
    const nonce = sessionStorage.getItem(GOOGLE_DRIVE_SESSION.NONCE);

    if (!code) {
      setError("No authorization code received. Try again from chat.");
      return;
    }

    if (!customerId) {
      setError("Session expired. Open Google Drive again from chat.");
      return;
    }

    if (state && nonce && state !== nonce) {
      setError("Invalid OAuth state. Please try connecting again.");
      return;
    }

    if (!isAuthenticated || !idToken) {
      navigate("/login", {
        replace: true,
        state: {
          returnUrl: `/connect/google-drive/callback${window.location.search}`,
        },
      });
      return;
    }

    const redirectUri = getGoogleDriveOAuthRedirectUri();

    exchangeStartedRef.current = true;
    void (async () => {
      try {
        const exchange = await exchangeGoogleDriveToken(
          code,
          customerId,
          idToken,
          redirectUri
        );
        // Remember the connected profile so the picker access-token lookup can target
        // the exact stored secret (avoids a ListSecrets the backend role can't do).
        rememberGoogleDriveProfileId(customerId, exchange?.profile_id);
        // Tell the chat window to open the picker itself on return, instead of
        // requiring a second click on "Google Drive" now that we're connected.
        // Only a chat-initiated flow arms the auto-open. A Connect-tab connect must
        // not make the Picker pop open the next time the user opens chat.
        if (sessionStorage.getItem(GOOGLE_DRIVE_SESSION.AUTO_OPEN_INTENT)) {
          sessionStorage.setItem(GOOGLE_DRIVE_SESSION.AUTO_OPEN, customerId);
        }
        sessionStorage.removeItem(GOOGLE_DRIVE_SESSION.AUTO_OPEN_INTENT);
        sessionStorage.removeItem(GOOGLE_DRIVE_SESSION.CUSTOMER_ID);
        sessionStorage.removeItem(GOOGLE_DRIVE_SESSION.RETURN_TO);
        sessionStorage.removeItem(GOOGLE_DRIVE_SESSION.NONCE);
        setDone(true);
        navigate(returnTo.startsWith("/") ? returnTo : `/${returnTo}`, {
          replace: true,
        });
      } catch (e) {
        exchangeStartedRef.current = false;
        setError(
          e instanceof Error ? e.message : "Failed to connect Google Drive."
        );
      }
    })();
  }, [isLoading, isAuthenticated, idToken, navigate, done]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-theme-background p-6">
        <div className="w-full max-w-md rounded-2xl border border-theme-danger/30 bg-theme-surface p-6 text-center shadow-sm">
          <p className="text-sm text-theme-danger">{error}</p>
          <button
            type="button"
            onClick={() => navigate("/", { replace: true })}
            className="mt-4 px-4 py-2 bg-theme-primary text-white rounded-lg hover:bg-theme-primary-hover text-sm"
          >
            Back to app
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-theme-background p-6">
      <div className="w-12 h-12 border-4 border-theme-border border-t-theme-primary rounded-full animate-spin" />
      <p className="mt-4 text-sm text-theme-fg-muted">Connecting Google Drive…</p>
    </div>
  );
};

export default GoogleDriveCallback;
